import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Mail, MessageSquare, Send, Loader2, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";

export const InfobipStyleSender = () => {
  const [channel, setChannel] = useState<"email" | "sms">("email");
  const [isSending, setIsSending] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  // Email fields
  const [emailFrom, setEmailFrom] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");

  // SMS fields
  const [smsFrom, setSmsFrom] = useState("");
  const [smsTo, setSmsTo] = useState("");
  const [smsText, setSmsText] = useState("");

  // Fetch templates
  const { data: templates } = useQuery({
    queryKey: ["campaign-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaign_templates")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Filter templates based on current channel
  const availableTemplates = templates?.filter(
    (t) => t.type === channel || t.type === "both"
  ) || [];

  // Handle template selection
  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = templates?.find((t) => t.id === templateId);
    if (!template) return;

    if (channel === "email") {
      setEmailSubject(template.subject || "");
      setEmailBody(template.message);
    } else if (channel === "sms") {
      setSmsText(template.message);
    }
  };

  const sendMessage = async () => {
    setIsSending(true);
    try {
      if (channel === "email") {
        if (!emailTo || !emailSubject || !emailBody) {
          toast.error("Please fill in all email fields");
          return;
        }

        const { error } = await supabase.functions.invoke("send-notifications", {
          body: {
            to: emailTo.split(",").map(e => e.trim()),
            subject: emailSubject,
            html: emailBody.replace(/\n/g, "<br>"),
            type: "email",
          },
        });

        if (error) throw error;
        toast.success("Email sent successfully via Infobip!");
        setEmailTo("");
        setEmailSubject("");
        setEmailBody("");
      } else if (channel === "sms") {
        if (!smsTo || !smsText) {
          toast.error("Please fill in all SMS fields");
          return;
        }

        const { error } = await supabase.functions.invoke("send-notifications", {
          body: {
            to: [],
            subject: "",
            html: "",
            type: "sms",
            phoneNumbers: smsTo.split(",").map(p => p.trim()),
            message: smsText,
          },
        });

        if (error) throw error;
        toast.success("SMS sent successfully via Infobip!");
        setSmsTo("");
        setSmsText("");
      }
    } catch (error) {
      console.error("Send error:", error);
      toast.error(`Failed to send ${channel.toUpperCase()}`);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Card className="border-border/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="h-5 w-5 text-primary" />
          Send Message - Infobip Style
        </CardTitle>
        <CardDescription>
          Send messages via Email or SMS using Infobip infrastructure
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Channel Selector */}
        <div className="space-y-2">
          <Label>Channel</Label>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant={channel === "email" ? "default" : "outline"}
              onClick={() => setChannel("email")}
              className="w-full"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email
            </Button>
            <Button
              variant={channel === "sms" ? "default" : "outline"}
              onClick={() => setChannel("sms")}
              className="w-full"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              SMS
            </Button>
          </div>
        </div>

        {/* Template Selector */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Use Template (Optional)
          </Label>
          <Select value={selectedTemplateId} onValueChange={handleTemplateSelect}>
            <SelectTrigger>
              <SelectValue placeholder="Select a template to pre-fill" />
            </SelectTrigger>
            <SelectContent>
              {availableTemplates.length === 0 ? (
                <div className="p-2 text-sm text-muted-foreground">No templates available</div>
              ) : (
                availableTemplates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    <div className="flex items-center gap-2">
                      {template.type === "email" && <Mail className="h-3 w-3" />}
                      {template.type === "sms" && <MessageSquare className="h-3 w-3" />}
                      {template.type === "both" && <Send className="h-3 w-3" />}
                      <span>{template.name}</span>
                    </div>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Select a template to populate the form fields below
          </p>
        </div>

        {/* Email Form */}
        {channel === "email" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>From (Optional)</Label>
              <Input
                placeholder="sender@yourdomain.com"
                value={emailFrom}
                onChange={(e) => setEmailFrom(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Leave empty to use default sender</p>
            </div>
            <div className="space-y-2">
              <Label>To *</Label>
              <Input
                placeholder="recipient@example.com (comma-separated for multiple)"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input
                placeholder="Email subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Message Body *</Label>
              <Textarea
                placeholder="Your email message..."
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows={6}
                required
              />
            </div>
          </div>
        )}

        {/* SMS Form */}
        {channel === "sms" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>From (Optional)</Label>
              <Input
                placeholder="Sender ID"
                value={smsFrom}
                onChange={(e) => setSmsFrom(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Leave empty to use default sender</p>
            </div>
            <div className="space-y-2">
              <Label>To *</Label>
              <Input
                placeholder="+1234567890 (comma-separated for multiple)"
                value={smsTo}
                onChange={(e) => setSmsTo(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Message Text *</Label>
              <Textarea
                placeholder="Your SMS message (max 160 characters)..."
                value={smsText}
                onChange={(e) => setSmsText(e.target.value)}
                rows={4}
                maxLength={160}
                required
              />
              <p className="text-xs text-muted-foreground">
                {smsText.length}/160 characters
              </p>
            </div>
          </div>
        )}

        {/* Send Button */}
        <Button
          onClick={sendMessage}
          disabled={isSending}
          className="w-full"
          size="lg"
        >
          {isSending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Send {channel.toUpperCase()}
            </>
          )}
        </Button>

        {/* Info Badge */}
        <div className="flex items-center justify-center">
          <Badge variant="outline" className="text-xs">
            Powered by Infobip
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};
